import { useEffect, useState, type RefObject } from 'react';
import { Keyboard, Platform, type KeyboardEvent, type View } from 'react-native';

/**
 * Hauteur, en points, dont le clavier recouvre le BAS de la vue `racine` (0 clavier fermé).
 *
 * Sert à un formulaire qui défile : il ajoute cette hauteur sous son contenu pour que le dernier
 * champ puisse remonter au-dessus du clavier, puis fait défiler jusqu'au champ actif. On mesure le
 * recouvrement réel (bas de la vue contre haut du clavier) au lieu de prendre la hauteur du clavier :
 * - la vue peut finir au-dessus d'une barre d'onglets, le clavier n'en recouvre alors que la partie
 *   qui dépasse ;
 * - si le système redimensionne déjà la fenêtre, le recouvrement est nul et rien n'est ajouté
 *   (sinon on doublerait le décalage).
 *
 * iOS : `keyboardWillChangeFrame` suit aussi un changement de hauteur en cours de saisie (barre de
 * suggestions, autre clavier). Android : `keyboardDidShow`, le seul événement d'ouverture.
 * Le web n'émet pas ces événements : le recouvrement y reste nul.
 */
export function useRecouvrementClavier(racine: RefObject<View | null>): number {
  const [recouvrement, setRecouvrement] = useState(0);

  useEffect(() => {
    const ios = Platform.OS === 'ios';
    const ouvre = Keyboard.addListener(ios ? 'keyboardWillChangeFrame' : 'keyboardDidShow', (e: KeyboardEvent) => {
      const { height, screenY } = e.endCoordinates;
      const vue = racine.current;
      if (!vue || typeof vue.measureInWindow !== 'function') {
        setRecouvrement(height);
        return;
      }
      vue.measureInWindow((_x, y, _largeur, hauteur) => {
        // Hors de [0, height] la mesure est aberrante (vue pas encore posée) : on borne.
        setRecouvrement(Math.min(height, Math.max(0, Math.round(y + hauteur - screenY))));
      });
    });
    const ferme = Keyboard.addListener(ios ? 'keyboardWillHide' : 'keyboardDidHide', () => setRecouvrement(0));
    return () => {
      ouvre.remove();
      ferme.remove();
    };
  }, [racine]);

  return recouvrement;
}
